import { useState, useCallback } from 'react';
import { youtubeService } from '@/shared/services/youtubeService';
import { sugestaoService } from '@/shared/services/sugestaoService';
import { sugestaoRepository } from '../../data/sugestaoRepository';
import type { YoutubeSearchResult } from '@/shared/models/playlist.model';

type StatusSugestao = 'idle' | 'buscando' | 'enviando' | 'aguardando-aprovacao' | 'erro';

export function useSugestaoBusca(
  empresaId: string,
  telaId: string,
  sessionId?: string,
  clienteId?: string | null,
) {
  const [resultadosBusca, setResultadosBusca] = useState<YoutubeSearchResult[]>([]);
  const [status, setStatus] = useState<StatusSugestao>('idle');
  const [erro, setErro] = useState('');

  const buscarNoYoutube = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResultadosBusca([]);
      return;
    }

    setStatus('buscando');
    setErro('');

    try {
      const resultados = await youtubeService.buscarVideos(query, { telaId });
      setResultadosBusca(resultados);
      setStatus('idle');
    } catch {
      setErro('Falha ao buscar no YouTube. Tente novamente.');
      setStatus('erro');
      setResultadosBusca([]);
    }
  }, [telaId]);

  const enviarSugestao = useCallback(async (video: YoutubeSearchResult, playlistItens: { video_id: string }[]) => {
    if (!empresaId || !telaId) return;

    setStatus('enviando');
    setErro('');

    try {
      // Verificar se música já está na playlist
      const jaExiste = playlistItens.some(item => item.video_id === video.videoId);
      if (jaExiste) {
        setStatus('erro');
        setErro('Esta música já está na playlist oficial.');
        return;
      }

      // Verificar se já existe sugestão pendente deste vídeo
      const jaSugerida = await sugestaoService.verificarDuplicada(empresaId, telaId, video.videoId);
      
      if (jaSugerida) {
        setStatus('erro');
        setErro('Esta música já foi sugerida e está aguardando aprovação.');
        return;
      }

      await sugestaoRepository.enviarSugestao(
        empresaId,
        telaId,
        video.videoId,
        video.titulo,
        video.thumbnail,
        video.canal,
        video.duracao_segundos,
        sessionId,
        clienteId,
      );

      setStatus('aguardando-aprovacao');
      setErro('');
      setResultadosBusca([]);

      setTimeout(() => {
        setStatus('idle');
      }, 5000);
    } catch (error) {
      setStatus('erro');
      setErro('Falha ao enviar sugestão. Tente novamente.');
      console.error('Erro ao enviar sugestão:', error);
    }
  }, [empresaId, telaId, sessionId, clienteId]);

  const limparBusca = useCallback(() => {
    setResultadosBusca([]);
    setStatus('idle');
    setErro('');
  }, []);

  return {
    resultadosBusca,
    status,
    erro,
    buscarNoYoutube,
    enviarSugestao,
    limparBusca,
  };
}
